import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router, RouterStateSnapshot } from '@angular/router';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, finalize, mergeMap, shareReplay, take } from 'rxjs/operators';
import { Doctor } from 'src/API';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { LanguageCode } from 'src/app/translations/language.model';
import { TranslationsService } from 'src/app/translations/translations.service';
import { LoadingSpinnerService } from '../../shared/loading-spinner/loading-spinner.service';
import { StaffService } from '../api/staff.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { StaffPermissionsService } from '../services/staff-permissions.service';

@Injectable({
  providedIn: 'root'
})
export class LoggedInUserResolver implements Resolve<Doctor> {
  private resolvedNavigationId: number = null;
  private resolvedStaff$: Observable<Doctor> = null;

  constructor(
    private staffService: StaffService,
    private loadingSpinnerService: LoadingSpinnerService,
    private authenticationService: AuthenticationService,
    private clinicSetupService: ClinicSetupService,
    private translationsService: TranslationsService,
    private staffPermissionsService: StaffPermissionsService,
    private router: Router
  ) {}

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<Doctor> | Observable<never> {
    const currentNavigation = this.router.getCurrentNavigation();
    const navigationId = currentNavigation ? currentNavigation.id : null;

    if (navigationId !== null && navigationId === this.resolvedNavigationId && this.resolvedStaff$) {
      return this.resolvedStaff$;
    }

    this.resolvedNavigationId = navigationId;
    this.resolvedStaff$ = this.loadLoggedInStaff().pipe(shareReplay(1));

    return this.resolvedStaff$;
  }

  private loadLoggedInStaff(): Observable<Doctor> {
    this.loadingSpinnerService.show();
    return this.staffService.getLoggedInStaff().pipe(
      take(1),
      mergeMap(staff => {
        if (staff) {
          this.staffService.staff = staff;

          this.translationsService.changeLanguage(
            (staff.preferredLanguage as LanguageCode) || LanguageCode.en
          );

          this.clinicSetupService.setClinicSetup(staff);
          this.staffPermissionsService.updateFromLoggedInStaff();

          return of(staff);
        } else {
          this.rejectAndRedirect();
          return EMPTY;
        }
      }),
      catchError(error => {
        console.dir(error);
        this.rejectAndRedirect();
        return EMPTY;
      }),
      finalize(() => this.loadingSpinnerService.hide())
    );
  }

  private rejectAndRedirect() {
    alert('You are not authorized to view this or it is not found.');
    this.authenticationService.logout().subscribe();
  }
}
